/**
 * ThemePresetSelect - Theme preset dropdown loaded from database
 * Adapted from shadcn-studio for OptiDev
 */

import { useMemo, useCallback, useState } from "react";

import { Dices, FileCode } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/hint";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

import { CssImportDialog } from "./css-import-dialog";

export interface Theme {
  id: string;
  themeId: string;
  name: string;
  colors: string[];
  content: string; // JSON string
  type: "built-in" | "custom";
}

interface ThemePresetSelectProps {
  themes: Theme[];
  currentPreset: string | null;
  onPresetChange: (themeId: string) => void;
  onCssImport?: (css: string) => void;
  isImportedTheme?: boolean; // True when CSS was imported but not saved
}

export function ThemePresetSelect({
  themes,
  currentPreset,
  onPresetChange,
  onCssImport,
  isImportedTheme,
}: ThemePresetSelectProps) {
  // State for CSS import dialog
  const [cssImportOpen, setCssImportOpen] = useState(false);

  const sortedThemes = useMemo(() => {
    // Separate built-in and custom themes
    const builtIn = themes.filter((t) => t.type === "built-in");
    const custom = themes.filter((t) => t.type === "custom");

    // Sort built-in alphabetically
    builtIn.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );
    custom.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );

    return { builtIn, custom };
  }, [themes]);

  // Helper function to get theme color from preview colors
  const getThemeColor = (theme: Theme, index: number): string => {
    return theme.colors[index] || "#888888";
  };

  // Randomize the preset
  const randomize = useCallback(() => {
    const allThemes = [...sortedThemes.builtIn, ...sortedThemes.custom];
    if (allThemes.length === 0) return;
    const random = Math.floor(Math.random() * allThemes.length);
    onPresetChange(allThemes[random].themeId);
  }, [onPresetChange, sortedThemes]);

  const value = themes.find((t) => t.themeId === currentPreset)?.themeId;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <Select value={value || ""} onValueChange={onPresetChange}>
          <SelectTrigger className="h-9 flex-1 cursor-pointer bg-muted/30 border-0 text-sm">
          {isImportedTheme && !value ? (
            <span className="flex items-center gap-2">
              <span className="size-2 rounded-full bg-primary animate-pulse" />
              Imported Theme
            </span>
          ) : (
            <SelectValue placeholder="Choose Theme" />
          )}
        </SelectTrigger>
        <SelectContent>
          {/* Built-in Themes */}
          {sortedThemes.builtIn.length > 0 && (
            <SelectGroup>
              <SelectLabel>Built-in Themes</SelectLabel>
              {sortedThemes.builtIn.map((theme) => (
                <SelectItem
                  key={theme.themeId}
                  value={theme.themeId}
                  className="flex items-center gap-3"
                >
                  <div className="flex items-center gap-3">
                    {/* Theme Color Grid Icon */}
                    <div className="flex items-center">
                      <div className="relative size-[22px] rounded-md overflow-hidden">
                        <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-px">
                          <div
                            className="rounded-[2px]"
                            style={{ backgroundColor: getThemeColor(theme, 0) }}
                          />
                          <div
                            className="rounded-[2px]"
                            style={{ backgroundColor: getThemeColor(theme, 1) }}
                          />
                          <div
                            className="rounded-[2px]"
                            style={{ backgroundColor: getThemeColor(theme, 2) }}
                          />
                          <div
                            className="rounded-full"
                            style={{ backgroundColor: getThemeColor(theme, 3) }}
                          />
                        </div>
                      </div>
                    </div>
                    <span>{theme.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          )}

          {/* Custom Themes */}
          {sortedThemes.custom.length > 0 && (
            <SelectGroup>
              <SelectLabel>Custom Themes</SelectLabel>
              {sortedThemes.custom.map((theme) => (
                <SelectItem
                  key={theme.themeId}
                  value={theme.themeId}
                  className="flex items-center gap-3"
                >
                  <div className="flex items-center gap-3">
                    {/* Theme Color Grid Icon */}
                    <div className="flex items-center">
                      <div className="relative size-[22px] rounded-md overflow-hidden">
                        <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-px">
                          <div
                            className="rounded-[2px]"
                            style={{ backgroundColor: getThemeColor(theme, 0) }}
                          />
                          <div
                            className="rounded-[2px]"
                            style={{ backgroundColor: getThemeColor(theme, 1) }}
                          />
                          <div
                            className="rounded-[2px]"
                            style={{ backgroundColor: getThemeColor(theme, 2) }}
                          />
                          <div
                            className="rounded-full"
                            style={{ backgroundColor: getThemeColor(theme, 3) }}
                          />
                        </div>
                      </div>
                    </div>
                    <span>{theme.name}</span>
                    <Badge variant="outline" className="ml-1">
                      Custom
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
        {onCssImport && (
          <Hint text="Import CSS" delayDuration={0}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCssImportOpen(true)}
              className="h-9 px-2.5 cursor-pointer text-muted-foreground hover:text-foreground"
            >
              <FileCode className="size-3.5" />
            </Button>
          </Hint>
        )}
        <Hint text="Random theme" delayDuration={0}>
          <Button
            variant="ghost"
            size="sm"
            onClick={randomize}
            className="h-9 px-2.5 cursor-pointer text-muted-foreground hover:text-foreground"
          >
            <Dices className="size-3.5" />
          </Button>
        </Hint>
      </div>
      <CssImportDialog
        open={cssImportOpen}
        onOpenChange={setCssImportOpen}
        onImport={(css) => onCssImport?.(css)}
      />
    </div>
  );
}
